import React from "react";
import {Box, Stack, Tooltip, Typography, useMediaQuery, useTheme} from "@mui/material";
import {grey, red} from "@mui/material/colors";
import {HugeiconsIcon} from "@hugeicons/react";
import {CallMissed02Icon, CheckIcon, CircleIcon, MessageSquareIcon} from "@hugeicons/core-free-icons";
import {useNavigate} from "react-router-dom";
import {Swiper, SwiperSlide} from "swiper/react";
import {Pagination} from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

const StatBlock = ({icon, iconColor, count, label, onClick}) => (
    <Stack
        onClick={onClick}
        alignItems="center"
        justifyContent="center"
        sx={{
            bgcolor: grey[50],
            borderRadius: 2,
            py: 2,
            px: 1,
            flex: 1,
            cursor: onClick ? "pointer" : "default",
            transition: "transform 0.15s ease",
            "&:hover": onClick ? {transform: "scale(1.03)"} : {},
        }}
    >
        <HugeiconsIcon icon={icon} size={30} color={iconColor}/>
        <Typography sx={{fontSize: 17, fontWeight: 500, mt: 1, mb: 0.25, color: grey[900]}}>
            {count}
        </Typography>
        <Typography sx={{fontSize: 11, color: grey[500]}}>
            {label}
        </Typography>
    </Stack>
);

export const ConversationStatusGauge = ({project, resolved, unresolved, missedCalls, tagUsage}) => {
    const isVoice = (project?.settings?.projectType || "VOICE_BOT") === "VOICE_BOT";
    const unresolvedExcludingMissed = unresolved - missedCalls;
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    // this needs to be endpoint fetch (backend)
    const startedToday = tagUsage?.main_menu || 0;

    return (
        <Stack
            direction="column"
            gap={0}
            sx={{borderRadius: 3, p: 1.5}}
            className={"border-shadow"}
        >
            <Typography variant={"body1"} color={grey[500]} sx={{mb: 1}}>
                CONVERSATION STATUS
            </Typography>

            {isMobile ? (
                <Box sx={{
                    minWidth: 0,
                    overflow: "hidden",
                    "& .swiper-pagination": {position: "static", mt: 1, lineHeight: 1},
                    "& .swiper-pagination-bullet": {width: 6, height: 6},
                    "& .swiper-pagination-bullet-active": {bgcolor: "primary.main"},
                }}>
                    <Swiper modules={[Pagination]} pagination={{clickable: true}}
                            slidesPerView={1} spaceBetween={12} style={{width: "100%"}}>
                        {isVoice ? (
                            <SwiperSlide>
                                <Tooltip title={"Show Missed Calls"}>
                                    <StatBlock
                                        icon={CallMissed02Icon}
                                        iconColor={missedCalls ? red[500] : grey[400]}
                                        count={missedCalls}
                                        label="Missed calls"
                                        onClick={() => navigate(`/project/${project?.id}/supervision?status=${missedCalls ? "MISSED_CALL" : ""}`)}
                                    />
                                </Tooltip>
                            </SwiperSlide>
                        ) : (
                            <SwiperSlide>
                                <StatBlock
                                    icon={MessageSquareIcon}
                                    iconColor={grey[400]}
                                    count={startedToday}
                                    label="Total"
                                />
                            </SwiperSlide>
                        )}
                        <SwiperSlide>
                            <StatBlock
                                icon={CheckIcon}
                                iconColor={grey[400]}
                                count={resolved}
                                label="Resolved"
                            />
                        </SwiperSlide>
                        <SwiperSlide>
                            <StatBlock
                                icon={CircleIcon}
                                iconColor={grey[400]}
                                count={unresolvedExcludingMissed}
                                label="Unresolved"
                            />
                        </SwiperSlide>
                    </Swiper>
                </Box>
            ) : (
                <Stack direction="row" gap={1.5} flex={1}>
                    {isVoice && (
                        <Tooltip title={"Show Missed Calls"}>
                            <StatBlock
                                icon={CallMissed02Icon}
                                iconColor={missedCalls ? red[500] : grey[400]}
                                count={missedCalls}
                                label="Missed calls"
                                onClick={() => navigate(`/project/${project?.id}/supervision?status=${missedCalls ? "MISSED_CALL" : ""}`)}
                            />
                        </Tooltip>
                    )}
                    {!isVoice && (
                        <StatBlock
                            icon={MessageSquareIcon}
                            iconColor={grey[400]}
                            count={startedToday}
                            label="Total"
                        />
                    )}
                    <StatBlock
                        icon={CheckIcon}
                        iconColor={grey[400]}
                        count={resolved}
                        label="Resolved"
                    />
                    <StatBlock
                        icon={CircleIcon}
                        iconColor={grey[400]}
                        count={unresolvedExcludingMissed}
                        label="Unresolved"
                    />
                </Stack>
            )}
        </Stack>
    );
};
